// Ultra Modern Glassmorphism Theme
export const theme = {
  // ============================================
  // COLORS - Cold Storage Theme
  // ============================================
  colors: {
    // Primary Colors - Deep Blue/Ice Theme
    primary: {
      50: '#e3f0ff',
      100: '#bad1f0',
      200: '#8bb4e0',
      300: '#5a97d0',
      400: '#367bc2',
      500: '#1a5fa8', // Primary
      600: '#0f4d91',
      700: '#083a75',
      800: '#042859',
      900: '#01173d',
    },
    
    // Secondary Colors - Fresh Mint/Cyan
    secondary: {
      50: '#e6faff',
      100: '#b3f0ff',
      200: '#80e5ff',
      300: '#4ddbff',
      400: '#1ad0ff',
      500: '#00b8e6', // Secondary
      600: '#0093b8',
      700: '#006e8a',
      800: '#004a5c',
      900: '#00252e',
    },
    
    // Accent Colors - Warm Orange (for highlights)
    accent: {
      50: '#fff3e0',
      100: '#ffe0b2',
      200: '#ffcc80',
      300: '#ffb74d',
      400: '#ffa726',
      500: '#ff9800', // Accent
      600: '#f57c00',
      700: '#e65100',
      800: '#bf360c',
      900: '#871f00',
    },
    
    // Success - Ice Green
    success: {
      50: '#e8f5e9',
      100: '#c8e6c9',
      200: '#a5d6a7',
      300: '#81c784',
      400: '#66bb6a',
      500: '#4caf50',
      600: '#388e3c',
      700: '#2e7d32',
      800: '#1b5e20',
      900: '#0a3010',
    },
    
    // Warning - Amber
    warning: {
      50: '#fff8e1',
      100: '#ffecb3',
      200: '#ffe082',
      300: '#ffd54f',
      400: '#ffca28',
      500: '#ffc107',
      600: '#ffb300',
      700: '#ffa000',
      800: '#ff8f00',
      900: '#ff6f00',
    },
    
    // Danger - Ice Red
    danger: {
      50: '#ffebee',
      100: '#ffcdd2',
      200: '#ef9a9a',
      300: '#e57373',
      400: '#ef5350',
      500: '#f44336',
      600: '#d32f2f',
      700: '#c62828',
      800: '#b71c1c',
      900: '#7f0000',
    },
    
    // Info - Light Blue
    info: {
      50: '#e1f5fe',
      100: '#b3e5fc',
      200: '#81d4fa',
      300: '#4fc3f7',
      400: '#29b6f6',
      500: '#03a9f4',
      600: '#039be5',
      700: '#0288d1',
      800: '#0277bd',
      900: '#01579b',
    },
    
    // Glassmorphism Colors
    glass: {
      light: {
        background: 'rgba(255, 255, 255, 0.15)',
        border: 'rgba(255, 255, 255, 0.2)',
        shadow: 'rgba(31, 38, 135, 0.15)',
        text: '#ffffff',
        textSecondary: 'rgba(255, 255, 255, 0.7)',
        hover: 'rgba(255, 255, 255, 0.25)',
      },
      dark: {
        background: 'rgba(13, 30, 55, 0.6)',
        border: 'rgba(255, 255, 255, 0.1)',
        shadow: 'rgba(0, 0, 0, 0.3)',
        text: '#e0e8f0',
        textSecondary: 'rgba(224, 232, 240, 0.7)',
        hover: 'rgba(255, 255, 255, 0.1)',
      }
    },
    
    // Gradients
    gradients: {
      primary: 'linear-gradient(135deg, #1a5fa8 0%, #00b8e6 100%)',
      secondary: 'linear-gradient(135deg, #00b8e6 0%, #4ddbff 100%)',
      accent: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
      dark: 'linear-gradient(135deg, #01173d 0%, #0a3050 100%)',
      glass: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
      glassDark: 'linear-gradient(135deg, rgba(13,30,55,0.6) 0%, rgba(13,30,55,0.3) 100%)',
    },
    
    // Backgrounds
    backgrounds: {
      main: '#0a1628',
      secondary: '#0d1e37',
      tertiary: '#132a48',
      card: 'rgba(255, 255, 255, 0.05)',
      glass: 'rgba(255, 255, 255, 0.08)',
      dark: '#061020',
    }
  },

  // ============================================
  // GLASSMORPHISM EFFECTS
  // ============================================
  glass: {
    // Light Glass Effect
    light: {
      background: 'rgba(255, 255, 255, 0.12)',
      backdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
      borderRadius: '20px',
    },
    // Dark Glass Effect
    dark: {
      background: 'rgba(13, 30, 55, 0.65)',
      backdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
      borderRadius: '20px',
    },
    // Frost Glass Effect
    frost: {
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(10px) saturate(120%)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      boxShadow: '0 4px 20px 0 rgba(31, 38, 135, 0.1)',
      borderRadius: '16px',
    },
    // Premium Glass Effect
    premium: {
      background: 'rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(30px) saturate(200%)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 12px 48px 0 rgba(31, 38, 135, 0.2)',
      borderRadius: '24px',
    }
  },

  // ============================================
  // TYPOGRAPHY
  // ============================================
  typography: {
    fontFamily: {
      primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      mono: "'JetBrains Mono', 'Courier New', monospace",
      display: "'Poppins', 'Inter', sans-serif",
    },
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem',// 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem',    // 48px
      '6xl': '3.75rem', // 60px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      none: 1,
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2,
    }
  },

  // ============================================
  // SPACING
  // ============================================
  spacing: {
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    8: '2rem',      // 32px
    10: '2.5rem',   // 40px
    12: '3rem',     // 48px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
    24: '6rem',     // 96px
    32: '8rem',     // 128px
    40: '10rem',    // 160px
    48: '12rem',    // 192px
    56: '14rem',    // 224px
    64: '16rem',    // 256px
  },

  // ============================================
  // BORDER RADIUS
  // ============================================
  borderRadius: {
    none: '0',
    sm: '0.25rem',   // 4px
    base: '0.375rem', // 6px
    md: '0.5rem',    // 8px
    lg: '0.75rem',   // 12px
    xl: '1rem',      // 16px
    '2xl': '1.5rem', // 24px
    '3xl': '2rem',   // 32px
    full: '9999px',
  },

  // ============================================
  // SHADOWS
  // ============================================
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    glass: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
    glassDark: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
    glow: '0 0 40px rgba(26, 95, 168, 0.3)',
  },

  // ============================================
  // TRANSITIONS
  // ============================================
  transition: {
    duration: {
      fastest: '100ms',
      faster: '200ms',
      fast: '300ms',
      normal: '400ms',
      slow: '500ms',
      slower: '600ms',
      slowest: '800ms',
    },
    timing: {
      linear: 'linear',
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
      bezier: 'cubic-bezier(0.4, 0, 0.2, 1)',
    }
  },

  // ============================================
  // ANIMATIONS
  // ============================================
  animations: {
    fadeIn: 'fadeIn 0.5s ease-in-out',
    slideUp: 'slideUp 0.5s ease-out',
    slideDown: 'slideDown 0.5s ease-out',
    slideLeft: 'slideLeft 0.5s ease-out',
    slideRight: 'slideRight 0.5s ease-out',
    scale: 'scale 0.3s ease-in-out',
    pulse: 'pulse 2s infinite',
    shimmer: 'shimmer 2s infinite',
    float: 'float 3s ease-in-out infinite',
    glassPulse: 'glassPulse 4s ease-in-out infinite',
  },

  // ============================================
  // Z-INDEX
  // ============================================
  zIndex: {
    auto: 'auto',
    0: '0',
    10: '10',
    20: '20',
    30: '30',
    40: '40',
    50: '50',
    dropdown: '1000',
    sticky: '1020',
    fixed: '1030',
    modal: '1040',
    popover: '1050',
    tooltip: '1060',
    overlay: '1070',
    max: '9999',
  }
};
/* Light Theme Variables */


// Theme Colors for CSS Variables
export const cssVariables = {
  '--color-primary': theme.colors.primary[500],
  '--color-primary-dark': theme.colors.primary[700],
  '--color-primary-light': theme.colors.primary[300],
  '--color-secondary': theme.colors.secondary[500],
  '--color-accent': theme.colors.accent[500],
  '--color-success': theme.colors.success[500],
  '--color-warning': theme.colors.warning[500],
  '--color-danger': theme.colors.danger[500],
  '--color-info': theme.colors.info[500],
  '--glass-background': 'rgba(255,255,255,0.08)',
  '--glass-border': 'rgba(255,255,255,0.12)',
  '--glass-shadow': '0 8px 32px rgba(31,38,135,0.15)',
  '--glass-blur': 'blur(20px) saturate(180%)',
};


export default theme;